import { CommonModule } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DynamicDialogRef } from '../../../../core/services/dialog';
import { DownloadService } from '../../../../core/services/download/download.service';
import {
  DownloadRecord,
  RecentDownloadsService,
} from '../../../../core/services/recent-downloads/recent-downloads.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { UtilitiesService } from '../../../../core/services/utilities/utilities.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { AccessibleClickDirective } from '../../../directives/accessible-click.directive';
import { IcDownloadComponent } from '../../Icons/ic-download/ic-download.component';
import { TextButton } from '../../buttons/text-button/text-button';
import { Card } from '../../cards/card/card';
import { DialogContentLayout } from '../../dialog/dialog-content-layout/dialog-content-layout';

/**
 * RecentDownloadsDialogComponent component.
 * Provides UI presentation and event handling for recent downloads dialog component.
 */
@Component({
  selector: 'app-recent-downloads-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogContentLayout,
    TranslatePipe,
    AccessibleClickDirective,
    IcDownloadComponent,
    Card,
    TextButton,
  ],
  templateUrl: './recent-downloads-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./recent-downloads-dialog.component.scss'],
})
export class RecentDownloadsDialogComponent {
  /** Injected service for dialog ref. */
  private dialogRef = inject(DynamicDialogRef);
  /** Injected service for recent downloads service. */
  private recentDownloadsService = inject(RecentDownloadsService);
  /** Injected service for utils. */
  private utils = inject(UtilitiesService);
  /** Injected service for download service. */
  private downloadService = inject(DownloadService);
  /** Injected service for toast service. */
  private toastService = inject(ToastService);

  /** Property downloads. */
  downloads = this.recentDownloadsService.downloads;

  /**
   * Handles on open folder logic.
   * @param record - The record
   */
  onOpenFolder(record: DownloadRecord) {
    if (record.apiPath) {
      this.downloadService
        .downloadDoc(record.apiPath, record.downloadParams as any)
        .subscribe({
          next: (blob: Blob) => {
            this.utils.saveFile(
              blob,
              record.filename,
              record.type,
              record.apiPath,
              record.downloadParams
            );
          },
          error: () => {
            this.toastService.showError('Error', 'oopsSomethingWentWrong');
          },
        });
    } else if (record.url && !record.url.startsWith('blob:')) {
      const a = document.createElement('a');
      a.href = record.url;
      a.download = record.filename;
      a.click();
    } else {
      this.toastService.showWarning('Warning', 'fileNoLongerAvailable');
    }
  }

  /**
   * Handles format date logic.
   * @param timestamp - The timestamp
   */
  formatDate(timestamp: number): string {
    return this.utils.formatToZonedTime(timestamp, 'dd MMM, HH:mm');
  }

  /**
   * Handles close logic.
   */
  close() {
    this.dialogRef.close();
  }

  /**
   * Handles clear all logic.
   */
  clearAll() {
    this.recentDownloadsService.clearHistory();
  }
}
